import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MODALIDADE, type Modalidade } from '@arena/shared/dominio';
import {
  ROTULO_MODALIDADE_NO_FORMULARIO,
  zPedidoDeReserva,
  type FormularioPublico,
} from '@arena/shared/schemas';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { siteApi } from '../api/site.js';
import { errosPorCampo, primeiraMensagem } from '../alunos/formatos.js';
import { dataCurta, dinheiro, rotuloDeDuracao } from '../agenda/formatos.js';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Formulario, Nota } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';

/**
 * O pedido que a recepção anota — "ligou pedindo sábado às 20h".
 *
 * Este modal substitui o formulário público: era por ele que o pedido
 * entrava na fila, e agora é por aqui. O que ele acrescenta ao que o
 * formulário fazia é justamente o que a recepção tem e o visitante não
 * tinha: a lista de horários LIVRES à mão, do mesmo cálculo que a agenda
 * usa — ocupação de agora, antecedência, horário de funcionamento e a
 * alocação fixa das turmas.
 *
 * Ele NÃO reserva. O pedido nasce pendente, e quem tira o horário da
 * grade é o encaixe, em `#m-pedido`. É deliberado: quem está ao telefone
 * raramente tem todas as respostas (quantas pessoas, qual quadra, quem
 * paga), e anotar é mais rápido que decidir.
 */

const DURACOES = [60, 90, 120];

export function ModalDeNovoPedido({ onFechar }: { onFechar: () => void }) {
  const avisar = useToast();
  const cliente = useQueryClient();

  // Os horários livres dos próximos dias, com o preço da hora — a mesma
  // conta que o formulário público usava.
  const livres = useQuery({ queryKey: ['site', 'previa'], queryFn: siteApi.previa });

  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [modalidade, setModalidade] = useState<Modalidade>('Futvolei');
  const [data, setData] = useState('');
  const [inicio, setInicio] = useState('');
  const [duracao, setDuracao] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [falha, setFalha] = useState<string | null>(null);

  const registrar = useMutation({
    mutationFn: siteApi.registrar,
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['site'] }),
  });

  const f: FormularioPublico | undefined = livres.data;
  const dias = f?.dias ?? [];
  const doDia = dias.find((d) => d.data === data);
  // Locação livre só cabe onde a agenda aceita locação; aula experimental
  // cabe em qualquer horário livre.
  const soLocacao = modalidade === 'Livre';
  const horarios = (doDia?.horarios ?? []).filter((h) => !soLocacao || h.locacao);

  async function salvar() {
    setErros({});
    setFalha(null);
    const pedido = zPedidoDeReserva.safeParse({
      nome: nome.trim(),
      telefone,
      modalidade,
      data,
      inicio,
      ...(duracao ? { duracaoMinutos: Number(duracao) } : {}),
      ...(mensagem.trim() ? { mensagem: mensagem.trim() } : {}),
    });
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }
    try {
      await registrar.mutateAsync(pedido.data);
      avisar(`Pedido de ${nome.trim().split(' ')[0]} na fila. Responda para reservar a quadra.`);
      onFechar();
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setFalha(mensagemDoErro(erro));
    }
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="estreita"
      titulo="Novo pedido de horário"
      descricao="O que chegou por telefone, mensagem ou no balcão"
      rodape={
        <>
          <span className="left">O pedido entra na fila; a quadra só sai da grade no encaixe</span>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void salvar()} disabled={registrar.isPending}>
            {registrar.isPending ? 'Salvando…' : 'Registrar pedido'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
          <Campo rotulo="Nome" erro={erros['nome']}>
            <input
              autoFocus
              value={nome}
              placeholder="Quem ligou"
              onChange={(e) => setNome(e.target.value)}
            />
          </Campo>
          <Campo
            rotulo="Telefone"
            ajuda="Obrigatório: é por ele que a arena responde."
            erro={erros['telefone']}
          >
            <input
              inputMode="tel"
              value={telefone}
              placeholder="(62) 9 0000-0000"
              onChange={(e) => setTelefone(e.target.value)}
            />
          </Campo>

          <Campo rotulo="Modalidade" erro={erros['modalidade']}>
            <select
              value={modalidade}
              onChange={(e) => {
                setModalidade(e.target.value as Modalidade);
                setInicio('');
              }}
            >
              {MODALIDADE.map((m) => (
                <option key={m} value={m}>
                  {ROTULO_MODALIDADE_NO_FORMULARIO[m]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo
            rotulo="Duração"
            ajuda={
              f && !duracao ? `Padrão da arena: ${rotuloDeDuracao(f.duracaoMinutos)}` : undefined
            }
            erro={erros['duracaoMinutos']}
          >
            <select value={duracao} onChange={(e) => setDuracao(e.target.value)}>
              <option value="">Padrão da arena</option>
              {DURACOES.map((m) => (
                <option key={m} value={m}>
                  {rotuloDeDuracao(m)}
                </option>
              ))}
            </select>
          </Campo>

          <Campo rotulo="Dia" erro={erros['data']}>
            <select
              value={data}
              onChange={(e) => {
                setData(e.target.value);
                setInicio('');
              }}
            >
              <option value="">Escolha o dia</option>
              {dias.map((d) => (
                <option key={d.data} value={d.data}>
                  {dataCurta(d.data)}
                  {d.horarios.length === 0 ? ' · sem horário livre' : ''}
                </option>
              ))}
            </select>
          </Campo>
          <Campo
            rotulo="Horário"
            ajuda={
              data && horarios.length === 0
                ? soLocacao
                  ? 'Nenhum horário livre para locação neste dia.'
                  : 'Nenhum horário livre neste dia.'
                : 'Só horários livres na agenda agora.'
            }
            erro={erros['inicio']}
          >
            <select value={inicio} disabled={!data} onChange={(e) => setInicio(e.target.value)}>
              <option value="">Escolha o horário</option>
              {horarios.map((h) => (
                <option key={h.inicio} value={h.inicio}>
                  {h.inicio}
                  {soLocacao && h.precoLocacao !== undefined
                    ? ` · ${dinheiro(h.precoLocacao)}`
                    : ''}
                  {h.descontoPercentual !== undefined ? ` · −${h.descontoPercentual}%` : ''}
                </option>
              ))}
            </select>
          </Campo>
        </Formulario>

        <Campo rotulo="Observação" erro={erros['mensagem']}>
          <textarea
            rows={2}
            value={mensagem}
            placeholder="O que a pessoa disse — 8 pessoas, quer a quadra coberta…"
            onChange={(e) => setMensagem(e.target.value)}
          />
        </Campo>

        {livres.isError && (
          <Nota>
            Não consegui carregar os horários livres: {mensagemDoErro(livres.error)}{' '}
            <button type="button" className="link" onClick={() => void livres.refetch()}>
              Tentar de novo
            </button>
            . Sem eles não há o que escolher nos dois campos acima.
          </Nota>
        )}

        {falha && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{falha}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}
